<template>
    <a-layout style="min-height: 100vh;">
        <UserHead />
        <a-layout>
            <a-layout-header style="padding: 0; background: #fff; border-bottom: 1px solid #f0f0f0;">
                <UserHeadTop />
            </a-layout-header>
            <a-layout-content class="shop-info-content-div">
                <div class="info-card">
                    <!-- 标题 -->
                    <div class="card-header">
                        <div class="header-icon">
                            <ShopOutlined />
                        </div>
                        <div class="header-text">
                            <h2>小店信息</h2>
                            <p>管理您的小店基本信息与配置</p>
                        </div>
                    </div>

                    <!-- 小店URL -->
                    <div class="info-box url-box" v-if="shopInfo.tenant_key">
                        <div class="box-icon">
                            <LinkOutlined />
                        </div>
                        <div class="box-content">
                            <div class="box-label">小店访问地址</div>
                            <div class="box-value">{{ shopUrl }}</div>
                        </div>
                        <div class="box-action">
                            <a-button size="small" @click="copyText(shopUrl)">复制</a-button>
                            <a-button size="small" type="primary" ghost @click="openShop">打开</a-button>
                        </div>
                    </div>

                    <!-- 余额 -->
                    <div class="info-box balance-box">
                        <div class="box-icon">
                            <WalletOutlined />
                        </div>
                        <div class="box-content">
                            <div class="box-label">账户余额</div>
                            <div class="box-value">￥{{ shopInfo.balance || '0.00' }}</div>
                        </div>
                    </div>

                    <!-- 支付方式备注 -->
                    <div class="pay-mode-notice warning" v-if="shopInfo.order_pay_mode === 'tenant'">
                        <div class="notice-icon warning">
                            <WarningOutlined />
                        </div>
                        <p>当前小店为<strong>自定义支付通道</strong>，游客访问小店后，将使用您自定义的支付通道进行支付，请确保余额充足，否则即使支付成功，余额支付开通授权时也会失败。
                        </p>
                    </div>
                    <div class="pay-mode-notice info" v-else-if="shopInfo.order_pay_mode === 'system'">
                        <div class="notice-icon info">
                            <InfoCircleOutlined />
                        </div>
                        <p>当前小店为<strong>系统支付通道</strong>，游客访问小店后，将使用系统支付通道进行支付，您自定义的支付通道不会生效，游客支付授权成功后，将发放反利给您。
                        </p>
                    </div>

                    <!-- 表单 -->
                    <div class="form-section">
                        <div class="form-section-title">基本设置</div>
                        <a-form :model="shopInfo" layout="vertical">
                            <a-form-item label="小店名称">
                                <a-input v-model:value="shopInfo.shop_name" placeholder="请输入小店名称" />
                            </a-form-item>
                            <a-form-item label="小店状态">
                                <a-switch v-model:checked="shopInfo.shop_status" :checked-value="1"
                                    :un-checked-value="0" checked-children="营业中" un-checked-children="已关闭" />
                            </a-form-item>
                            <a-form-item label="小店简介">
                                <a-textarea v-model:value="shopInfo.shop_desc" :rows="3" show-count
                                    placeholder="请输入小店简介" />
                            </a-form-item>
                            <a-form-item label="联系方式">
                                <a-input v-model:value="shopInfo.contact" placeholder="请输入联系方式（如QQ、微信、邮箱等）" />
                            </a-form-item>
                            <a-form-item label="支付模式" v-if="isAdmin">
                                <a-radio-group v-model:value="shopInfo.order_pay_mode">
                                    <a-radio value="tenant">自定义支付通道</a-radio>
                                    <a-radio value="system">系统支付通道</a-radio>
                                </a-radio-group>
                            </a-form-item>
                            <a-form-item>
                                <a-button type="primary" class="save-btn" @click="saveShopInfo" :loading="saving">
                                    保存修改
                                </a-button>
                            </a-form-item>
                        </a-form>
                    </div>
                </div>
            </a-layout-content>
        </a-layout>
    </a-layout>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import axios from 'axios'
import { message } from 'ant-design-vue'
import { ShopOutlined, LinkOutlined, WalletOutlined, WarningOutlined, InfoCircleOutlined } from '@ant-design/icons-vue'
import UserHead from '@/components/UserHead.vue'
import UserHeadTop from '@/components/UserHeadTop.vue'

const baseURL = process.env.VUE_APP_API_BASE_URL
const headers = { 'token': localStorage.getItem('token') || '' }

const shopInfo = ref({
    id: 0,
    tenant_key: '',
    shop_name: '',
    shop_status: 1,
    shop_desc: '',
    contact: '',
    balance: 0,
    order_pay_mode: '',
})
const saving = ref(false)
const isAdmin = ref(false)

const shopUrl = computed(() => {
    if (!shopInfo.value.tenant_key) return ''
    const origin = window.location.origin
    return `${origin}/shop?tenant_key=${shopInfo.value.tenant_key}`
})

const getShopInfo = async () => {
    try {
        const res = await axios.post(baseURL + '/api/shop/tenant/info', {}, { headers })
        if (res.data.code === 0) {
            shopInfo.value = { ...shopInfo.value, ...res.data.data }
            isAdmin.value = !!res.data.data.is_admin
        } else {
            message.error(res.data.msg || '获取小店信息失败')
        }
    } catch (e: any) {
        message.error('获取小店信息失败：' + e.message)
    }
}

const saveShopInfo = async () => {
    saving.value = true
    try {
        const res = await axios.post(baseURL + '/api/shop/tenant/update', {
            shop_name: shopInfo.value.shop_name,
            shop_status: shopInfo.value.shop_status,
            shop_desc: shopInfo.value.shop_desc,
            contact: shopInfo.value.contact,
            ...(isAdmin.value ? { order_pay_mode: shopInfo.value.order_pay_mode } : {}),
        }, { headers })
        if (res.data.code === 0) {
            message.success('保存成功')
        } else {
            message.error(res.data.msg || '保存失败')
        }
    } catch (e: any) {
        message.error('保存失败：' + e.message)
    } finally {
        saving.value = false
    }
}

const copyText = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
        message.success('已复制到剪贴板')
    }).catch(() => {
        message.warning('复制失败，请手动复制')
    })
}

const openShop = () => {
    window.open(shopUrl.value, '_blank')
}

onMounted(() => {
    getShopInfo()
})
</script>

<style scoped lang="scss">
.shop-info-content-div {
    height: 100vh;
    background: linear-gradient(180deg, #f8fafc 0%, #eef2ff 60%, #f8fafc 100%);
    padding: 1.5rem;
    overflow-y: auto;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
        width: 0;
        height: 0;
        display: none;
    }
}

.info-card {
    background: #ffffff;
    border-radius: 1.2rem;
    border: 1px solid rgba(226, 232, 240, 0.9);
    box-shadow: 0 12px 32px -20px rgba(15, 23, 42, 0.2);
    padding: 2rem;
    max-width: 680px;
    margin: 0 auto;
}

.card-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 2rem;
    padding-bottom: 1.25rem;
    border-bottom: 1px solid #f1f5f9;

    .header-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 42px;
        height: 42px;
        border-radius: 0.75rem;
        background: linear-gradient(135deg, #2563eb, #3b82f6);
        color: #fff;
        font-size: 1.15rem;
    }

    .header-text {
        h2 {
            font-size: 1.3rem;
            font-weight: 700;
            color: #0f172a;
            margin: 0;
        }

        p {
            font-size: 0.82rem;
            color: #94a3b8;
            margin: 0.15rem 0 0;
        }
    }
}

/* 信息展示卡片 */
.info-box {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem 1.2rem;
    border-radius: 0.8rem;
    margin-bottom: 1rem;

    .box-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 36px;
        height: 36px;
        border-radius: 0.6rem;
        flex-shrink: 0;
        font-size: 1rem;
    }

    .box-content {
        flex: 1;
        min-width: 0;
    }

    .box-label {
        font-size: 0.75rem;
        color: #94a3b8;
        font-weight: 500;
        margin-bottom: 0.15rem;
    }

    .box-value {
        font-size: 0.9rem;
        color: #1e293b;
        word-break: break-all;
    }

    .box-action {
        flex-shrink: 0;
        display: flex;
        gap: 0.4rem;
    }
}

.info-box.key-box {
    background: #eff6ff;
    border: 1px solid #bfdbfe;

    .box-icon {
        background: #dbeafe;
        color: #2563eb;
    }

    .box-value {
        font-family: monospace;
        color: #1e40af;
    }
}

.info-box.url-box {
    background: #f0fdf4;
    border: 1px solid #bbf7d0;

    .box-icon {
        background: #dcfce7;
        color: #16a34a;
    }

    .box-value {
        font-family: monospace;
        color: #15803d;
        font-size: 0.82rem;
    }
}

.info-box.balance-box {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #fcd34d;

    .box-icon {
        background: #fde68a;
        color: #d97706;
    }

    .box-value {
        font-size: 1.3rem;
        font-weight: 700;
        color: #b45309;
    }
}

/* 表单区域 */
.form-section {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid #f1f5f9;
}

/* 支付方式备注 */
.pay-mode-notice {
    display: flex;
    align-items: flex-start;
    gap: 0.6rem;
    padding: 0.9rem 1rem;
    border-radius: 0.7rem;
    margin-bottom: 1rem;
    margin-top: 0.5rem;

    .notice-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 28px;
        height: 28px;
        border-radius: 0.5rem;
        flex-shrink: 0;
        font-size: 0.9rem;

        &.warning {
            background: #fef3c7;
            color: #d97706;
        }

        &.info {
            background: #dbeafe;
            color: #2563eb;
        }
    }

    p {
        margin: 0;
        font-size: 0.82rem;
        line-height: 1.6;
        color: #475569;

        strong {
            font-weight: 600;
        }
    }

    &.warning {
        background: #fffbeb;
        border: 1px solid #fde68a;

        p strong {
            color: #b45309;
        }
    }

    &.info {
        background: #eff6ff;
        border: 1px solid #bfdbfe;

        p strong {
            color: #1e40af;
        }
    }
}

.form-section-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 1.25rem;
    display: flex;
    align-items: center;
    gap: 0.4rem;

    &::before {
        content: '';
        width: 3px;
        height: 14px;
        background: #2563eb;
        border-radius: 2px;
    }
}

.save-btn {
    width: 100%;
    height: 44px;
    font-size: 0.95rem;
    border-radius: 0.6rem;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    border: none;
}

@media screen and (max-width: 768px) {
    .shop-info-content-div {
        padding: 1rem;
    }

    .info-card {
        padding: 1.2rem;
        border-radius: 1rem;
    }

    .card-header {
        .header-icon {
            width: 36px;
            height: 36px;
        }

        .header-text h2 {
            font-size: 1.1rem;
        }
    }

    .info-box {
        padding: 0.75rem 0.9rem;
        flex-wrap: wrap;
    }
}
</style>
